import React from "react";

import { authorizedFetch } from "./boot.js";
import { Card, Stat, Tag } from "./ui.jsx";
import { OwnerInvitations } from "./owner_invitations.jsx";
import { BoxAssignments } from "./box_assignments.jsx";
import { AdminCameras } from "./cameras.jsx";
import { AdminRepositoryStats } from "./admin_repository_stats.jsx";
import { SharedInspectorBook } from "./inspector_book.jsx";
import { sydneyWhen } from "./time.js";

const { useEffect, useMemo, useState } = React;

const ADMIN_SECTION = "inglis-admin";
const AREA_KEYS = ["A/B", "B/C", "C/D", "D/E", "H/I", "I/J", "J/K", "K"];

function query(params) {
  const values = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") values.set(key, value);
  });
  const suffix = values.toString();
  return suffix ? `?${suffix}` : "";
}

async function collection(path) {
  const response = await authorizedFetch(`/collections/${path}`);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = body?.error?.code || body?.errors?.[0]?.code || `request failed (${response.status})`;
    const error = new Error(code);
    error.status = response.status;
    error.code = code;
    throw error;
  }
  return body;
}

function errorMessage(error, fallback = "The administration data could not be loaded.") {
  if (!error) return "";
  if (error.status === 503 || error.code === "identity_provider_unavailable") {
    return "Identity verification is temporarily unavailable. Wait 15 seconds before trying again.";
  }
  if (error.status === 412 || error.code === "stale_revision") {
    return "This allocation changed elsewhere. Reloaded the current revision; review your changes before saving again.";
  }
  if (error.status === 409) return "That save was already used. Refresh the allocation before trying again.";
  if (error.status === 400) return "The selected sale, vendor or allocation is not valid.";
  if (error.status === 404) return "That record is no longer available to this administrator.";
  return fallback;
}

function acceptedRefreshErrorMessage(error) {
  if (error?.status === 503 || error?.code === "identity_provider_unavailable") {
    return "Allocation saved, but identity verification is temporarily unavailable. Wait 15 seconds before retrying the read.";
  }
  return "Allocation saved, but the current revision could not be loaded. Retry the read before making another change.";
}

function useRequest(request, deps, enabled = true) {
  const [state, setState] = useState({ busy: false, data: null, error: null });
  const [retryVersion, setRetryVersion] = useState(0);
  useEffect(() => {
    let live = true;
    if (!enabled) {
      setState({ busy: false, data: null, error: null });
      return () => { live = false; };
    }
    setState((current) => ({ ...current, busy: true, error: null }));
    request().then((data) => {
      if (live) setState({ busy: false, data, error: null });
    }).catch((error) => {
      if (live) setState({ busy: false, data: null, error });
    });
    return () => { live = false; };
  // The caller supplies stable scalar keys. The request itself is deliberately
  // not a dependency: a new function on each render must not refetch forever.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, retryVersion]);
  return { ...state, retry: () => setRetryVersion((current) => current + 1) };
}

function Empty({ children }) {
  return <p className="muted admin-empty" role="status">{children}</p>;
}

function ErrorState({ error, fallback, retry, retryLabel = "Retry", message, disabled = false }) {
  return <div className="error-text" role="alert">
    <p>{message || errorMessage(error, fallback)}</p>
    {retry ? <button type="button" className="btn sm" aria-label={retryLabel} onClick={retry} disabled={disabled}>{retryLabel}</button> : null}
  </div>;
}

function Overview() {
  const state = useRequest(() => collection("admin.overview@1/"), []);
  if (state.busy && !state.data) return <p role="status">Loading the Inglis overview…</p>;
  if (state.error) return <ErrorState error={state.error} fallback="The Inglis overview is unavailable." retry={state.retry} retryLabel="Retry overview"/>;
  const data = state.data || { counts: {} };
  const counts = data.counts || {};
  return (
    <>
      <div className="grid cols-4 admin-stat-grid">
        <Card><Stat label="Vendors" value={counts.vendors ?? 0}/></Card>
        <Card><Stat label="Sales" value={counts.sales ?? 0}/></Card>
        <Card><Stat label="Lots" value={counts.lots ?? 0}/></Card>
        <Card><Stat label="People" value={counts.people ?? 0}/></Card>
      </div>
      <Card title="Parade access" subtitle="Sale-specific vendor allocations">
        <p>{counts.paradeAllocations ?? 0} allocation{counts.paradeAllocations === 1 ? "" : "s"} recorded.</p>
        <p className="caption">{AREA_KEYS.join(" · ")}</p>
      </Card>
      <Card title="Cameras" subtitle="Parade areas and Stable K boxes">
        <p className="muted">Open either camera tab to watch. Camera access is available 24 hours during a live sale.</p>
      </Card>
    </>
  );
}

function Sales() {
  const [search, setSearch] = useState("");
  const state = useRequest(
    () => collection(`admin.sales.list@1/${query({ q: search, limit: 50, offset: 0 })}`),
    [search],
  );
  return (
    <Card title="Sales" subtitle="Sale registry and mirror status">
      <label className="field"><span>Search sales</span>
        <input aria-label="Search sales" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by sale"/>
      </label>
      {state.busy && !state.data && <p role="status">Loading sales…</p>}
      {state.error && <ErrorState error={state.error} fallback="The sales registry is unavailable." retry={state.retry} retryLabel="Retry sales"/>}
      {state.data && state.data.items?.length === 0 && <Empty>No sales are registered yet.</Empty>}
      {state.data && state.data.items?.length > 0 && (
        <div className="table-wrap"><table className="table"><thead><tr><th>Sale</th><th>Lifecycle</th><th>Lots</th><th>Vendors</th></tr></thead>
          <tbody>{state.data.items.map((sale) => <tr key={sale.id}>
            <td><strong>{sale.id}</strong></td><td>{sale.lifecycle || "—"}</td><td>{sale.lotCount ?? 0}</td><td>{sale.vendorCount ?? 0}</td>
          </tr>)}</tbody>
        </table></div>
      )}
    </Card>
  );
}

function Vendors() {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const list = useRequest(
    () => collection(`admin.vendors.list@1/${query({ q: search, limit: 50, offset: 0 })}`),
    [search],
  );
  const detail = useRequest(
    () => collection(`admin.vendors.detail@1/${query({ vendor: selected })}`),
    [selected],
    Boolean(selected),
  );
  const masquerade = (vendor) => {
    if (vendor.canMasquerade !== true) return;
    const url = new URL(window.location.href);
    url.searchParams.set("realm", vendor.id);
    window.history.replaceState({}, "", url.toString());
    window.location.reload();
  };
  return (
    <div className="admin-detail-grid">
      <Card title="Vendors" subtitle="Live vendor registry">
        <label className="field"><span>Search vendors</span>
          <input aria-label="Search vendors" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search vendors"/>
        </label>
        {list.busy && !list.data && <p role="status">Loading vendors…</p>}
        {list.error && <ErrorState error={list.error} fallback="The vendor registry is unavailable." retry={list.retry} retryLabel="Retry vendors"/>}
        {list.data && list.data.items?.length === 0 && <Empty>No vendors are registered yet.</Empty>}
        {list.data?.items?.map((vendor) => <button type="button" className={`admin-list-row ${selected === vendor.id ? "selected" : ""}`} key={vendor.id} onClick={() => setSelected(vendor.id)}>
          <span><strong>{vendor.name || vendor.id}</strong><small>{vendor.lotCount ?? 0} lots · {vendor.realmStatus || "unknown"}</small></span>
          <span>{vendor.canMasquerade === true ? <Tag variant="gold">Masquerade available</Tag> : null}</span>
        </button>)}
      </Card>
      <div><VendorDetail detail={detail} masquerade={masquerade}/>
        {selected && detail.data?.vendor?.id === selected && !detail.busy && !detail.error && <BoxAssignments key={`boxes:${selected}`} sale="26R2R" vendor={selected} admin/>}
        {selected && detail.data?.vendor?.id === selected && !detail.busy && !detail.error && <OwnerInvitations key={selected} vendor={selected} vendorName={detail.data.vendor.name}/>}
      </div>
    </div>
  );
}

function VendorDetail({ detail, masquerade }) {
  if (detail.busy) return <Card title="Vendor detail"><p role="status">Loading vendor detail…</p></Card>;
  if (!detail.data && !detail.error) return <Card title="Vendor detail"><Empty>Select a vendor to inspect its live records.</Empty></Card>;
  if (detail.error) return <Card title="Vendor detail"><ErrorState error={detail.error} fallback="The vendor detail is unavailable." retry={detail.retry} retryLabel="Retry vendor detail"/></Card>;
  const { vendor = {}, sales = [], lots = [], lotsTruncated } = detail.data;
  return <Card title={vendor.name || vendor.id || "Vendor detail"} subtitle="Registry detail and sale participation" right={vendor.canMasquerade === true ? <button type="button" className="btn sm primary" onClick={() => masquerade(vendor)}>Masquerade</button> : null}>
    <p className="muted">{vendor.realmStatus || "Realm status unavailable"} · {vendor.lotCount ?? lots.length} lots</p>
    <h4>Sales</h4>
    {sales.length ? <ul>{sales.map((sale) => <li key={sale.id}>{sale.id || sale.sale} · {sale.lifecycle || "—"}</li>)}</ul> : <Empty>No sale records for this vendor.</Empty>}
    <h4>Lots</h4>
    {lots.length ? <div className="table-wrap"><table className="table"><thead><tr><th>Lot</th><th>Sale</th><th>Status</th></tr></thead><tbody>{lots.map((lot) => <tr key={lot.id}><td>{lot.number || lot.id}</td><td>{lot.sale || "—"}</td><td>{lot.withdrawn ? "Withdrawn" : lot.reviewed ? "Reviewed" : "Unreviewed"}</td></tr>)}</tbody></table></div> : <Empty>No lot records for this vendor.</Empty>}
    {lotsTruncated ? <p className="caption">Some lots are omitted from this preview.</p> : null}
  </Card>;
}

function People() {
  const [search, setSearch] = useState("");
  const state = useRequest(() => collection(`admin.people.list@1/${query({ q: search, limit: 50, offset: 0 })}`), [search]);
  return <Card title="People" subtitle="Access across Inglis and vendor realms">
    <label className="field"><span>Search people</span><input aria-label="Search people" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search people"/></label>
    {state.busy && !state.data && <p role="status">Loading people…</p>}
    {state.error && <ErrorState error={state.error} fallback="The people directory is unavailable." retry={state.retry} retryLabel="Retry people"/>}
    {state.data && state.data.items?.length === 0 && <Empty>No people are registered yet.</Empty>}
    {state.data?.items?.length > 0 && <div className="table-wrap"><table className="table"><thead><tr><th>Name</th><th>Status</th><th>Memberships</th></tr></thead><tbody>{state.data.items.map((person) => <tr key={person.id}><td>{person.displayName || person.id}</td><td>{person.status || "—"}</td><td>{(person.memberships || []).map((membership) => membership.name || membership.realm).join(" · ") || "—"}</td></tr>)}</tbody></table></div>}
  </Card>;
}

function idempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `vd-admin-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function Parade() {
  const [sale, setSale] = useState("");
  const [vendor, setVendor] = useState("");
  const [areas, setAreas] = useState([]);
  const [savedAreas, setSavedAreas] = useState([]);
  const [revision, setRevision] = useState(null);
  const [availableAreas, setAvailableAreas] = useState([]);
  const [status, setStatus] = useState("");
  const [saveError, setSaveError] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [refreshPending, setRefreshPending] = useState(false);
  const [saving, setSaving] = useState(false);
  const sales = useRequest(() => collection(`admin.sales.list@1/${query({ limit: 50, offset: 0 })}`), [], true);
  const vendors = useRequest(() => collection(`admin.vendors.list@1/${query({ sale, limit: 50, offset: 0 })}`), [sale], true);
  const allocation = useRequest(() => collection(`admin.parade_allocations.get@2/${query({ sale, vendor })}`), [sale, vendor], Boolean(sale && vendor));
  const saleOptions = (sales.data?.items || []).filter((item) => ["DRAFT", "LIVE"].includes(String(item.lifecycle || "").toUpperCase()));
  const vendorOptions = (vendors.data?.items || []).filter((item) => !sale || !Array.isArray(item.saleIds) || item.saleIds.includes(sale));
  const selectSale = (value) => {
    setSale(value); setVendor(""); setAreas([]); setSavedAreas([]); setRevision(null); setAvailableAreas([]); setStatus(""); setSaveError(null); setRefreshError(null); setRefreshPending(false);
  };
  const selectVendor = (value) => {
    setVendor(value); setAreas([]); setSavedAreas([]); setRevision(null); setAvailableAreas([]); setStatus(""); setSaveError(null); setRefreshError(null); setRefreshPending(false);
  };
  useEffect(() => {
    if (!allocation.data) return;
    const next = Array.isArray(allocation.data.areas) ? allocation.data.areas : [];
    setAreas(next); setSavedAreas(next); setRevision(allocation.data.revision ?? 0);
    setAvailableAreas((allocation.data.availableAreas || []).filter((item) => AREA_KEYS.includes(item.key)));
    setStatus(""); setSaveError(null); setRefreshError(null); setRefreshPending(false);
  }, [allocation.data]);
  const dirty = useMemo(() => JSON.stringify(areas) !== JSON.stringify(savedAreas), [areas, savedAreas]);
  const toggle = (key) => setAreas((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key].filter((item) => AREA_KEYS.includes(item)));
  const applyAllocation = (next, targetSale, targetVendor, fallbackRevision) => {
    if (sale !== targetSale || vendor !== targetVendor) return false;
    const nextAreas = Array.isArray(next.areas) ? next.areas : [];
    setAreas(nextAreas); setSavedAreas(nextAreas); setRevision(next.revision ?? fallbackRevision);
    setAvailableAreas((next.availableAreas || []).filter((item) => AREA_KEYS.includes(item.key)));
    setRefreshError(null); setRefreshPending(false); setStatus("Allocation saved");
    return true;
  };
  const retryAcceptedRefresh = async () => {
    if (!sale || !vendor || !refreshPending || saving) return;
    const targetSale = sale;
    const targetVendor = vendor;
    setSaving(true); setRefreshError(null); setStatus("Refreshing the current revision…");
    try {
      const next = await collection(`admin.parade_allocations.get@2/${query({ sale: targetSale, vendor: targetVendor })}`);
      applyAllocation(next, targetSale, targetVendor, revision);
    } catch (error) {
      if (sale === targetSale && vendor === targetVendor) {
        setRefreshError(error); setStatus("");
      }
    } finally {
      setSaving(false);
    }
  };
  const save = async () => {
    if (!sale || !vendor || revision === null || !dirty || refreshPending) return;
    const targetSale = sale;
    const targetVendor = vendor;
    const expectedRevision = revision;
    let postAccepted = false;
    setSaving(true); setStatus("Saving…"); setSaveError(null);
    try {
      const response = await authorizedFetch("/actions/admin.parade_allocations.replace@2/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: { sale: targetSale, vendor: targetVendor, areas, expected_revision: expectedRevision }, idempotency_key: idempotencyKey() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const code = body?.error?.code || body?.errors?.[0]?.code || "save_failed";
        const error = new Error(code); error.status = response.status; error.code = code; throw error;
      }
      postAccepted = true;
      setRefreshPending(true); setRefreshError(null);
      setStatus("Saved. Refreshing the current revision…");
      const next = await collection(`admin.parade_allocations.get@2/${query({ sale: targetSale, vendor: targetVendor })}`);
      applyAllocation(next, targetSale, targetVendor, expectedRevision + 1);
    } catch (error) {
      if (postAccepted) {
        if (sale === targetSale && vendor === targetVendor) {
          setRefreshError(error); setStatus("");
        }
      } else {
        setSaveError(error); setStatus("");
        if (error.status === 412) {
          try {
            const next = await collection(`admin.parade_allocations.get@2/${query({ sale: targetSale, vendor: targetVendor })}`);
            applyAllocation(next, targetSale, targetVendor, expectedRevision);
            setStatus("");
          } catch (_) { /* retain the original bounded conflict message */ }
        }
      }
    } finally {
      setSaving(false);
    }
  };
  return <div className="admin-parade">
    <Card title="Parade access" subtitle="Allocations are scoped to one sale and one vendor">
      <div className="grid cols-2"><label className="field"><span>Sale</span><select aria-label="Parade sale" value={sale} onChange={(event) => selectSale(event.target.value)} disabled={saving || refreshPending}><option value="">Choose a sale…</option>{saleOptions.map((item) => <option key={item.id} value={item.id}>{item.id}</option>)}</select></label>
        <label className="field"><span>Vendor</span><select aria-label="Parade vendor" value={vendor} onChange={(event) => selectVendor(event.target.value)} disabled={saving || refreshPending}><option value="">Choose a vendor…</option>{vendorOptions.map((item) => <option key={item.id} value={item.id}>{item.name || item.id}</option>)}</select></label></div>
      {sales.error && <ErrorState error={sales.error} fallback="The sale registry is unavailable." retry={sales.retry} retryLabel="Retry sales"/>}
      {vendors.error && <ErrorState error={vendors.error} fallback="The vendor registry is unavailable." retry={vendors.retry} retryLabel="Retry vendors"/>}
      {sales.data && vendors.data && (saleOptions.length === 0 || vendorOptions.length === 0) ? <Empty>There are no active sale/vendor records to allocate yet.</Empty> : null}
      {!sale || !vendor ? <p className="muted">Select both records to review their allocation.</p> : null}
      {allocation.busy && <p role="status">Loading allocation…</p>}
      {allocation.error && <ErrorState error={allocation.error} fallback="This allocation is unavailable." retry={allocation.retry} retryLabel="Retry allocation"/>}
      {sale && vendor && allocation.data && allocation.data.sale === sale && allocation.data.vendor === vendor && <>
        <p className="caption">Revision {revision} · Camera access follows the saved allocation.</p>
        <div className="admin-allocation-grid">{availableAreas.map((item) => <label className="admin-area" key={item.key}><input type="checkbox" checked={areas.includes(item.key)} onChange={() => toggle(item.key)} disabled={saving || refreshPending}/><span>{item.label || item.key}</span></label>)}</div>
        {availableAreas.length === 0 ? <Empty>No parade areas are configured.</Empty> : null}
        {dirty && !refreshPending ? <p className="admin-dirty" role="status">Unsaved allocation changes</p> : null}
        {saveError ? <ErrorState error={saveError} fallback="The allocation could not be saved."/> : null}
        {refreshError ? <ErrorState error={refreshError} message={acceptedRefreshErrorMessage(refreshError)} retry={retryAcceptedRefresh} retryLabel="Retry allocation refresh" disabled={saving}/> : null}
        {status ? <p className="success-text" role="status">{status}</p> : null}
        <button type="button" className="btn primary" onClick={save} disabled={!dirty || revision === null || saving || refreshPending}>Save allocation</button>
      </>}
    </Card>
  </div>;
}

// The poller's own outcome vocabulary, in words. NOT_DUE never reaches here in
// practice — a run that decides it is not due writes no provenance row — but
// the whole closed set is mapped so a new one can never render as a raw code.
const POLL_OUTCOMES = {
  APPLIED: "Applied the catalogue",
  UNCHANGED: "Catalogue unchanged since the last read",
  SOURCE_UNAVAILABLE: "The Inglis API could not be read",
  SOURCE_DRIFT: "The Inglis API answered in an unexpected shape",
  NOT_DUE: "Not read; the ordinary cadence was not due",
};

const REVIEW_KINDS = {
  LOT_ADDED: "In the catalogue, not in the mirror",
  LOT_REMOVED: "In the mirror, not in the catalogue",
  LOT_VENDOR_CHANGED: "Catalogue places this lot with another vendor",
  VENDOR_RENAMED: "Catalogue names this vendor differently",
};

function syncMessage(error) {
  if (error?.status === 503 || error?.code === "sale_api_not_configured") {
    return "This environment has no Inglis API origin or credential configured, so nothing was read.";
  }
  if (error?.status === 409) return "That sync was already used. Reload before syncing again.";
  return errorMessage(error, "The sync could not be run.");
}

function SaleMirror() {
  const sale = "26R2R";
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const state = useRequest(() => collection(`admin.sale_mirror.pending_reviews@1/${query({ sale })}`), [sale]);
  const shown = outcome || state.data;
  const poll = shown?.lastPoll || null;

  const runSync = async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      const response = await authorizedFetch("/actions/admin.sale_mirror.sync@1/", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ input: { sale } }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(body?.error?.code || `request failed (${response.status})`);
        error.status = response.status;
        error.code = body?.error?.code;
        throw error;
      }
      // The receipt is a protocol result, never the data. What the run did is
      // read back through the governed projection the response carries.
      setOutcome(body.result || null);
    } catch (error) {
      setSyncError(error);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card title="Sale mirror" subtitle="Results, withdrawals, and the differences awaiting a decision">
      <p className="caption">
        The poller sets a lot&rsquo;s result and marks it withdrawn. It never creates, removes or
        re-assigns a lot, and never renames a vendor: those are listed below for you to act on.
      </p>
      <button type="button" className="btn primary" onClick={runSync} disabled={syncing}>
        {syncing ? "Syncing…" : "Sync now"}
      </button>
      {syncError ? <ErrorState error={syncError} message={syncMessage(syncError)}/> : null}
      {state.busy && !state.data && !outcome && <p role="status">Loading the sale mirror…</p>}
      {state.error && !outcome && <ErrorState error={state.error} fallback="The sale mirror is unavailable." retry={state.retry} retryLabel="Retry sale mirror"/>}
      {poll ? (
        <div className="grid cols-4 admin-stat-grid">
          <Card><Stat label="Last read" value={poll.fetchedAt ? sydneyWhen(poll.fetchedAt) : "Never"}/></Card>
          <Card><Stat label="Outcome" value={POLL_OUTCOMES[poll.outcome] || poll.outcome || "—"}/></Card>
          <Card><Stat label="Lots updated" value={poll.lotsUpdated ?? 0}/></Card>
          <Card><Stat label="Needing review" value={shown?.total ?? 0}/></Card>
        </div>
      ) : null}
      {poll ? (
        <p className="caption">
          Cadence {poll.cadence || "unknown"} ({poll.reason || "no reason recorded"}),
          run {poll.trigger === "manual" ? "by hand" : "on the schedule"}.
        </p>
      ) : null}
      {shown && (shown.reviews || []).length === 0 && <Empty>Nothing is waiting on a decision.</Empty>}
      {shown && (shown.reviews || []).length > 0 && (
        <div className="table-wrap"><table className="table">
          <thead><tr><th>Difference</th><th>Lot or vendor</th><th>Mirror</th><th>Catalogue</th><th>First seen</th></tr></thead>
          <tbody>{shown.reviews.map((row) => <tr key={`${row.kind}:${row.subject}`}>
            <td>{REVIEW_KINDS[row.kind] || row.kind}</td>
            <td><strong>{row.subject}</strong></td>
            <td>{row.mirrorVendor || "—"}</td>
            <td>{row.sourceVendor || "—"}</td>
            <td>{row.firstSeenAt ? sydneyWhen(row.firstSeenAt) : "—"}</td>
          </tr>)}</tbody>
        </table></div>
      )}
    </Card>
  );
}

function InglisAdminView() {
  const access = window.VD.access || {};
  const sections = window.VD.sections || [];
  const authorized = sections.includes(ADMIN_SECTION) && access.realm === "inglis";
  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get("adminView") || "overview");
  if (!authorized) return <div className="boot-state" role="status"><h1>Inglis administration unavailable</h1><p>This administration surface is not enabled for the selected realm.</p></div>;
  const views = { overview: <Overview/>, sales: <Sales/>, mirror: <SaleMirror/>, vendors: <Vendors/>, people: <People/>, inspectors: <SharedInspectorBook/>, parade: <Parade/>, cameras: <AdminCameras kind="parade"/>, boxes: <AdminCameras kind="box"/>, repository: <AdminRepositoryStats/> };
  const select = (next) => { setView(next); const url = new URL(window.location.href); url.searchParams.set("adminView", next); window.history.replaceState({}, "", url.toString()); };
  return <div className="admin-console">
    <div className="admin-console-head"><div><div className="eyebrow">Inglis operations</div><h2>Administration</h2></div><Tag variant="gold">Inglis realm</Tag></div>
    <nav className="admin-subnav" aria-label="Administration views">{[["overview", "Overview"], ["sales", "Sales"], ["mirror", "Sale mirror"], ["vendors", "Vendors"], ["people", "People"], ["inspectors", "Inspectors"], ["parade", "Parade access"], ["cameras", "Parade areas"], ["boxes", "Stable K boxes"], ["repository", "Repository stats"]].map(([id, label]) => <button type="button" key={id} className={view === id ? "active" : ""} onClick={() => select(id)}>{label}</button>)}</nav>
    {views[view] || views.overview}
  </div>;
}

export { InglisAdminView, AREA_KEYS };
